import React, { useMemo, useState } from 'react';
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Chip,
  Divider, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead,
  TablePagination, TableRow, TextField, Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { CheckCircleOutline, FactCheckOutlined, SkipNextOutlined } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { DB_FIELD_LABELS } from '../utils/importFields';
import { IMPORT_DATE_FORMATS, IMPORT_VALUE_CHOICES } from '../utils/importReview';

const STATUS_LABELS = { ready: 'Ready', review: 'Needs review', skipped: 'Skipped' };
const STATUS_COLORS = { ready: 'success', review: 'warning', skipped: 'default' };
const PAGE_SIZE = 25;
const EMPTY_ENTRIES = [];
const EMPTY_COMPLETED_INDEXES = new Set();
const isCellError = value => value && typeof value === 'object' && typeof value.__importCellError === 'string';
const displayValue = value => isCellError(value) ? value.originalText || value.__importCellError
  : value instanceof Date ? (Number.isNaN(value.getTime()) ? 'Invalid date' : value.toISOString().slice(0, 10))
    : String(value ?? '');
const valueLabel = value => displayValue(value) || '(blank)';
const fieldLabel = field => DB_FIELD_LABELS[field] || field;
const can = (issue, action) => (issue.allowedActions || []).includes(action);
const DATE_OPTION_LABELS = {
  auto: 'Auto-detect dates', 'yyyy-MM-dd': 'YYYY-MM-DD', 'd/M/yyyy': 'DD/MM/YYYY',
  'M/d/yyyy': 'MM/DD/YYYY', 'd.M.yyyy': 'DD.MM.YYYY', 'd-MMM-yy': 'DD-MMM-YY', 'd-MMM-yyyy': 'DD-MMM-YYYY',
};
const STATUS_DETAILS = {
  ready: { icon: CheckCircleOutline, description: 'Can be imported now.', color: 'success' },
  review: { icon: FactCheckOutlined, description: 'Waiting for your decision.', color: 'warning' },
  skipped: { icon: SkipNextOutlined, description: 'Missing or duplicate tags.', color: 'text' },
};
const accordionSx = {
  border: 1, borderColor: 'divider', borderRadius: '12px !important', minWidth: 0,
  '&:before': { display: 'none' },
  '& .MuiAccordionSummary-root': { minHeight: 64, px: { xs: 2, md: 2.5 } },
  '& .MuiAccordionSummary-content': { minWidth: 0 },
  '& .MuiAccordionDetails-root': { px: { xs: 2, md: 2.5 }, pb: 2.5, minWidth: 0 },
};
const selectSx = {
  minWidth: 0, width: '100%',
  '& .MuiInputBase-root': { backgroundColor: 'background.paper' },
  '& .MuiNativeSelect-select': { minWidth: 0, pr: '36px !important' },
};

function IssueActions({ issue, rowNumber, disabled, onResolve }) {
  const [replacement, setReplacement] = useState('');
  return (
    <Stack spacing={1} sx={{ mt: 1 }}>
      <Stack direction="row" useFlexGap flexWrap="wrap" spacing={1}>
        {can(issue, 'keep') && <Button size="small" disabled={disabled} onClick={() => onResolve({ action: 'keep' })}>
          Keep exact text
        </Button>}
        {can(issue, 'blank') && <Button size="small" disabled={disabled} onClick={() => onResolve({ action: 'blank' })}>
          Leave this field blank
        </Button>}
      </Stack>
      {can(issue, 'replace') && (
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ minWidth: 0 }}>
          <TextField
            size="small" label="Corrected value" InputLabelProps={{ shrink: true }}
            value={replacement} disabled={disabled} onChange={event => setReplacement(event.target.value)}
            inputProps={{ maxLength: 10000, 'aria-label': `Corrected ${fieldLabel(issue.field)} — row ${rowNumber}` }} sx={{ minWidth: 0, width: '100%', flex: 1 }}
          />
          <Button size="small" sx={{ flexShrink: 0, alignSelf: { xs: 'flex-start', md: 'center' } }} disabled={disabled || !replacement.trim()} onClick={() => onResolve({ action: 'replace', value: replacement })}>
            Apply correction
          </Button>
        </Stack>
      )}
    </Stack>
  );
}

export default function ImportReviewPanel({
  review, rows = [], mapping = {}, headers = [], options = {}, onOptionsChange,
  disabled = false, onDownloadExceptions, onDownloadReport, completedIndexes = EMPTY_COMPLETED_INDEXES,
}) {
  const entries = review?.entries || EMPTY_ENTRIES;
  const counts = review?.counts || { ready: 0, review: 0, skipped: 0 };
  const [filter, setFilter] = useState('review');
  const [page, setPage] = useState(0);
  const [bulkChoice, setBulkChoice] = useState({});
  const filtered = useMemo(() => entries.filter(entry => filter === 'all' || entry.status === filter), [entries, filter]);
  const lastPage = Math.max(0, Math.ceil(filtered.length / PAGE_SIZE) - 1);
  const activePage = Math.min(page, lastPage);
  const visible = filtered.slice(activePage * PAGE_SIZE, (activePage + 1) * PAGE_SIZE);
  const unmapped = headers.filter(header => !Object.values(mapping).includes(header));
  const sourceValues = useMemo(() => Object.fromEntries(Object.keys(IMPORT_VALUE_CHOICES).map(field => {
    const column = mapping[field];
    const values = new Map();
    if (column) rows.forEach(row => {
      if (isCellError(row[column])) return;
      const raw = displayValue(row[column]).trim();
      if (raw) values.set(raw, (values.get(raw) || 0) + 1);
    });
    return [field, [...values.entries()]];
  })), [rows, mapping]);
  const issueGroups = useMemo(() => {
    const groups = new Map();
    entries.filter(entry => entry.status === 'review' && !completedIndexes.has(entry.index)).forEach(entry => {
      (entry.issues || []).forEach(issue => {
        if (!can(issue, 'keep') && !can(issue, 'blank')) return;
        const key = `${issue.field}:${issue.code}`;
        if (!groups.has(key)) groups.set(key, { key, field: issue.field, code: issue.code, message: issue.message, entries: [], canKeep: true, canBlank: true });
        const group = groups.get(key);
        group.canKeep = group.canKeep && can(issue, 'keep');
        group.canBlank = group.canBlank && can(issue, 'blank');
        group.entries.push(entry);
      });
    });
    return [...groups.values()].filter(group => group.entries.length > 1);
  }, [entries, completedIndexes]);
  const decisionCount = entries.reduce((count, entry) => count + (entry.decisions?.length || 0), 0);
  const datePreview = mapping.inspection_date
    ? entries.filter(entry => !completedIndexes.has(entry.index) && displayValue(rows[entry.index]?.[mapping.inspection_date]).trim()).slice(0, 5)
    : [];
  const change = patch => onOptionsChange({ ...options, ...patch });
  const resolve = (entry, field, resolution) => {
    if (disabled || completedIndexes.has(entry.index)) return;
    change({ resolutions: {
      ...(options.resolutions || {}),
      [entry.rowNumber]: { ...(options.resolutions?.[entry.rowNumber] || {}), [field]: resolution },
    } });
  };
  const clearResolution = (entry, field) => {
    if (disabled || completedIndexes.has(entry.index)) return;
    const resolutions = { ...(options.resolutions || {}) };
    resolutions[entry.rowNumber] = { ...(resolutions[entry.rowNumber] || {}) };
    delete resolutions[entry.rowNumber][field];
    if (!Object.keys(resolutions[entry.rowNumber]).length) delete resolutions[entry.rowNumber];
    change({ resolutions });
  };
  const applyBulk = group => {
    if (disabled) return;
    const action = bulkChoice[group.key];
    if ((action !== 'keep' || !group.canKeep) && (action !== 'blank' || !group.canBlank)) return;
    const resolutions = { ...(options.resolutions || {}) };
    group.entries.forEach(entry => {
      if (completedIndexes.has(entry.index)) return;
      resolutions[entry.rowNumber] = { ...(resolutions[entry.rowNumber] || {}), [group.field]: { action } };
    });
    change({ resolutions });
    setBulkChoice(previous => ({ ...previous, [group.key]: '' }));
  };
  const setValueMapping = (field, raw, selection) => {
    const fieldMappings = { ...(options.valueMappings?.[field] || {}) };
    if (selection === '__unmapped') delete fieldMappings[raw];
    else fieldMappings[raw] = selection === '__blank' ? '' : selection;
    change({ valueMappings: { ...(options.valueMappings || {}), [field]: fieldMappings } });
  };

  return (
    <Paper id="import-review" component="section" aria-labelledby="import-review-title" variant="outlined" sx={{ p: { xs: 2.5, md: 3 }, borderRadius: 3, scrollMarginTop: 100, minWidth: 0 }}>
      <Stack spacing={3} sx={{ minWidth: 0 }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Box aria-hidden="true" sx={{ width: 36, height: 36, flexShrink: 0, display: 'grid', placeItems: 'center', borderRadius: 2, bgcolor: theme => alpha(theme.palette.primary.main, 0.09), color: 'primary.main', fontWeight: 800 }}>3</Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography id="import-review-title" variant="h6" fontWeight={750}>Review before importing</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 760 }}>
              Confirm the values below, then import the ready records. Anything needing attention stays here for review.
            </Typography>
          </Box>
        </Stack>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(3, minmax(0, 1fr))' }, gap: 1.5 }}>
          {Object.entries(STATUS_LABELS).map(([status, label]) => {
            const detail = STATUS_DETAILS[status];
            const Icon = detail.icon;
            const color = status === 'skipped' ? 'text.secondary' : `${detail.color}.main`;
            return <Box key={status} sx={{ p: { xs: 2, md: 2.5 }, border: 1, borderColor: 'divider', borderRadius: 2.5, minWidth: 0, bgcolor: theme => alpha(status === 'skipped' ? theme.palette.text.secondary : theme.palette[detail.color].main, 0.035) }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ color }}>
                <Icon sx={{ fontSize: 21, flexShrink: 0 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 750, overflowWrap: 'anywhere' }}>{label}: {(counts[status] || 0).toLocaleString()}</Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.6 }}>{detail.description}</Typography>
            </Box>;
          })}
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <Button variant="outlined" disabled={disabled || !(counts.review || counts.skipped) || !onDownloadExceptions} onClick={onDownloadExceptions}>
            Download exceptions
          </Button>
          <Button disabled={disabled || !entries.length || !onDownloadReport} onClick={onDownloadReport}>Download review report</Button>
        </Stack>

        {mapping.inspection_date && <Box sx={{ p: { xs: 2, md: 2.5 }, border: 1, borderColor: 'divider', borderRadius: 2.5, minWidth: 0 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1fr) minmax(240px, 0.8fr)' }, alignItems: 'center', gap: 2 }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle1" fontWeight={700}>Check your dates</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>Compare the original dates with the values EXREG will import.</Typography>
            </Box>
            <TextField select SelectProps={{ native: true }} size="small" fullWidth label="Source date format" InputLabelProps={{ shrink: true }}
              inputProps={{ 'aria-label': 'Source date format' }}
              helperText={IMPORT_DATE_FORMATS.find(option => option.value === (options.dateFormat || 'auto'))?.label}
              sx={selectSx} value={options.dateFormat || 'auto'} disabled={disabled} onChange={event => change({ dateFormat: event.target.value })}>
              {IMPORT_DATE_FORMATS.map(({ value, label }) => <option key={value} value={value}>{DATE_OPTION_LABELS[value] || label}</option>)}
            </TextField>
          </Box>
          {datePreview.length > 0 && <TableContainer sx={{ mt: 2, borderTop: 1, borderColor: 'divider', minWidth: 0 }}><Table size="small" aria-label="Date conversion preview" sx={{ '& th': { color: 'text.secondary', fontSize: 12, fontWeight: 600 }, '& td, & th': { px: { xs: 1, md: 2 }, py: 1.2 } }}>
            <TableHead><TableRow><TableCell>Source row</TableCell><TableCell>Original date</TableCell><TableCell>Date to import</TableCell></TableRow></TableHead>
            <TableBody>{datePreview.map(entry => <TableRow key={entry.rowNumber}>
              <TableCell>{entry.rowNumber}</TableCell><TableCell sx={{ whiteSpace: 'nowrap' }}>{valueLabel(rows[entry.index]?.[mapping.inspection_date])}</TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{entry.issues?.some(issue => issue.field === 'inspection_date') ? 'Needs review' : entry.status === 'skipped' ? 'Skipped' : valueLabel(entry.values?.inspection_date)}</TableCell>
            </TableRow>)}</TableBody>
          </Table></TableContainer>}
        </Box>}

        {Object.entries(sourceValues).some(([, values]) => values.length) && <Accordion disableGutters defaultExpanded elevation={0} sx={accordionSx}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography fontWeight={700}>Inspection value mapping</Typography></AccordionSummary>
          <AccordionDetails><Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">Choose how each source value should be recorded in EXREG. Values such as “Fixed” need your decision.</Typography>
            {Object.entries(sourceValues).map(([field, values]) => values.length > 0 && <Box key={field} sx={{ minWidth: 0 }}>
              <Typography variant="subtitle2" sx={{ mb: 1.5, color: 'text.secondary' }}>{fieldLabel(field)}</Typography>
              <Stack spacing={1.5}>{values.slice(0, 100).map(([raw, count]) => {
                const explicit = Object.prototype.hasOwnProperty.call(options.valueMappings?.[field] || {}, raw);
                const selected = explicit ? options.valueMappings[field][raw] || '__blank' : IMPORT_VALUE_CHOICES[field].includes(raw) ? raw : '__unmapped';
                return <Box key={raw} sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'minmax(0, 1fr) minmax(210px, 0.65fr)' }, gap: { xs: 1.5, sm: 3 }, alignItems: 'center', p: { xs: 1.5, md: 2 }, bgcolor: theme => alpha(theme.palette.text.secondary, 0.025), border: 1, borderColor: 'divider', borderRadius: 2, minWidth: 0 }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" color="text.secondary">From your file</Typography>
                    <Typography variant="body2" fontWeight={650} sx={{ overflowWrap: 'anywhere', mt: 0.25 }}>{raw}</Typography>
                    <Typography variant="caption" color="text.secondary">{count.toLocaleString()} records</Typography>
                  </Box>
                  <TextField select SelectProps={{ native: true }} size="small" fullWidth label="EXREG value" InputLabelProps={{ shrink: true }}
                    inputProps={{ 'aria-label': `${fieldLabel(field)}: ${raw}` }}
                    value={selected} disabled={disabled} onChange={event => setValueMapping(field, raw, event.target.value)} sx={selectSx}>
                    <option value="__unmapped">Choose a value</option>
                    {IMPORT_VALUE_CHOICES[field].map(value => <option key={value} value={value}>{value}</option>)}
                    <option value="__blank">Leave this field blank</option>
                  </TextField>
                </Box>;
              })}</Stack>
              {values.length > 100 && <Typography variant="caption">Showing the first 100 source values. Other values can be corrected in the records below.</Typography>}
            </Box>)}
          </Stack></AccordionDetails>
        </Accordion>}

        {unmapped.length > 0 && <Accordion disableGutters elevation={0} sx={accordionSx}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography sx={{ overflowWrap: 'anywhere' }}>{unmapped.length} source {unmapped.length === 1 ? 'column' : 'columns'} will not be imported</Typography></AccordionSummary>
          <AccordionDetails><Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>{unmapped.map(header => <Chip key={header} size="small" label={header} variant="outlined" sx={{ maxWidth: '100%', height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', overflowWrap: 'anywhere', py: 0.5 } }} />)}</Box></AccordionDetails>
        </Accordion>}

        {issueGroups.length > 0 && <Accordion disableGutters elevation={0} sx={accordionSx}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography fontWeight={700}>Resolve repeated issues together</Typography></AccordionSummary>
          <AccordionDetails><Stack spacing={2}>
            <Alert severity="info">Keeping exact text preserves the source value, including scientific notation. It does not recover missing digits. Leaving a field blank affects only that field. Every decision appears in the report.</Alert>
            {issueGroups.map(group => <Box key={group.key} sx={{ p: { xs: 1.5, md: 2 }, border: 1, borderColor: 'divider', borderRadius: 2, minWidth: 0 }}>
              <Typography variant="subtitle2">{fieldLabel(group.field)} — {group.entries.length.toLocaleString()} records</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 0.5, overflowWrap: 'anywhere' }}>{group.message}</Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <TextField select SelectProps={{ native: true }} size="small" label="Decision" InputLabelProps={{ shrink: true }} inputProps={{ 'aria-label': `Decision for ${fieldLabel(group.field)} (${group.code})` }}
                  value={bulkChoice[group.key] || ''} disabled={disabled} onChange={event => setBulkChoice(previous => ({ ...previous, [group.key]: event.target.value }))} sx={{ ...selectSx, flex: 1 }}>
                  <option value="">Choose a decision</option>
                  {group.canKeep && <option value="keep">Keep exact source text</option>}
                  {group.canBlank && <option value="blank">Leave this field blank</option>}
                </TextField>
                <Button variant="outlined" sx={{ flexShrink: 0, alignSelf: { xs: 'flex-start', sm: 'center' } }} disabled={disabled || !bulkChoice[group.key]} onClick={() => applyBulk(group)}>Apply to {group.entries.length.toLocaleString()} records</Button>
              </Stack>
            </Box>)}
          </Stack></AccordionDetails>
        </Accordion>}

        {decisionCount > 0 && <Typography variant="body2" color="text.secondary">{decisionCount.toLocaleString()} explicit field decisions will be included in the review report.</Typography>}
        <Divider />
        <TextField select SelectProps={{ native: true }} size="small" label="Show records" InputLabelProps={{ shrink: true }} inputProps={{ 'aria-label': 'Show records' }} value={filter}
          onChange={event => { setFilter(event.target.value); setPage(0); }} sx={{ ...selectSx, maxWidth: 320 }}>
          <option value="review">Needs review ({counts.review || 0})</option>
          <option value="ready">Ready ({counts.ready || 0})</option>
          <option value="skipped">Skipped ({counts.skipped || 0})</option>
          <option value="all">All ({entries.length})</option>
        </TextField>
        {!filtered.length && <Alert severity={filter === 'review' ? 'success' : 'info'}>
          {filter === 'review' ? 'No records need review. Choose Ready to preview the records that can be imported.' : 'No records in this view.'}
        </Alert>}
        {filtered.length > 0 && <>
          <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 2, minWidth: 0 }}><Table size="small" aria-label="Import record review" sx={{ tableLayout: 'fixed', minWidth: 560, '& th': { bgcolor: theme => alpha(theme.palette.text.secondary, 0.04), color: 'text.secondary', fontWeight: 650 } }}>
            <TableHead><TableRow><TableCell sx={{ width: 135 }}>Source row / tag</TableCell><TableCell sx={{ width: 110 }}>Status</TableCell><TableCell>Details</TableCell></TableRow></TableHead>
            <TableBody>{visible.map(entry => <TableRow key={entry.rowNumber} data-import-row={entry.rowNumber} sx={{ '& td': { verticalAlign: 'top', py: 2 } }}>
              <TableCell><Typography variant="body2">Row {entry.rowNumber}</Typography><Typography variant="caption" sx={{ overflowWrap: 'anywhere' }}>{entry.tag || '(no tag)'}</Typography></TableCell>
              <TableCell><Chip size="small" variant="outlined" color={completedIndexes.has(entry.index) ? 'success' : STATUS_COLORS[entry.status]} label={completedIndexes.has(entry.index) ? 'Already imported' : STATUS_LABELS[entry.status]} sx={{ height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', py: 0.5 } }} /></TableCell>
              <TableCell><Stack spacing={2}>
                {(entry.issues || []).map((issue, index) => <Box key={`${issue.field}:${issue.code}:${index}`}>
                  <Typography variant="subtitle2">{fieldLabel(issue.field)}</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', p: 1, bgcolor: theme => alpha(theme.palette.text.secondary, 0.045), borderRadius: 1 }}>Source: {valueLabel(issue.rawValue)}</Typography>
                  <Typography variant="body2" color="text.secondary">{issue.message}</Typography>
                  {(entry.status === 'review' || (entry.status === 'skipped' && issue.field === 'equipment_tag' && ['missing_tag', 'duplicate_tag'].includes(issue.code))) && <IssueActions key={`${entry.rowNumber}:${issue.field}:${issue.code}`} issue={entry.status === 'skipped' ? { ...issue, allowedActions: ['replace'] } : issue} rowNumber={entry.rowNumber} disabled={disabled || completedIndexes.has(entry.index)} onResolve={resolution => resolve(entry, issue.field, resolution)} />}
                </Box>)}
                {(entry.decisions || []).map((decision, index) => <Box key={`${decision.field}:${index}`}>
                  <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>{fieldLabel(decision.field)}: {valueLabel(decision.originalValue)} → {valueLabel(decision.value)} ({decision.action === 'keep' ? 'kept exact text' : decision.action === 'blank' ? 'left blank' : decision.action === 'map' ? 'mapped value' : 'corrected'})</Typography>
                  {decision.action !== 'map' && <Button size="small" disabled={disabled || completedIndexes.has(entry.index)} onClick={() => clearResolution(entry, decision.field)}>Undo decision</Button>}
                </Box>)}
                {entry.status === 'ready' && <Stack spacing={0.5}>
                  {['serial_no', 'inspection_date', 'inspection_result', 'inspection_type'].filter(field => mapping[field]).map(field => <Typography key={field} variant="body2" sx={{ overflowWrap: 'anywhere' }}>{fieldLabel(field)}: {valueLabel(entry.values?.[field])}</Typography>)}
                  {!(entry.decisions || []).length && !['serial_no', 'inspection_date', 'inspection_result', 'inspection_type'].some(field => mapping[field]) && <Typography variant="body2">Ready to import.</Typography>}
                </Stack>}
              </Stack></TableCell>
            </TableRow>)}</TableBody>
          </Table></TableContainer>
          <TablePagination component="div" sx={{ '& .MuiTablePagination-toolbar': { px: 0, flexWrap: 'wrap' }, '& .MuiTablePagination-spacer': { flex: '1 1 0%' }, '& .MuiTablePagination-displayedRows': { ml: 0 } }} count={filtered.length} page={activePage} onPageChange={(_event, nextPage) => setPage(nextPage)} rowsPerPage={PAGE_SIZE} rowsPerPageOptions={[PAGE_SIZE]} />
        </>}
      </Stack>
    </Paper>
  );
}
